import reactWrapper from "@harbor/elements/utils/react/wrapper";
import { CaretDown, User, Users, UserCircle } from "phosphor-react";
import "@magnetic/cross-platform-navigator";
import { PropTypes } from "prop-types";
import React from "react";
import { useI18n } from "../../../i18n/useI18n";

//utils
import { deleteCookie, deleteCookies, getCookie } from "../../../serviceWorker";
import { removeRumUser } from "../../../monitoring";
import { userProfileStyles } from "../appHeaderConfig";
import {
  removeItemsLocalStorage,
  removeLocalStorageFlag,
  getLocalStorageFlag,
  parseVersion,
} from "../../../utils/common";
import { checkCookieValidity } from "../../../utils/waniCommon";

//config
import apiService from "../../../config/api-config";
import { PROFILE_URL } from "../../../apis/apiConstants";
import {
  applyTheme,
  THEME_STORAGE_KEY,
  ThemeModeToggle,
  ThemeModes,
} from "../../../common/Theme";
import { routeURLs, VERSION_THRESHOLDS } from "../../../utils/enums";

//style
import css from "../../genericMagneticStyle.less";
import { useEffect } from "react";

const [HbrButton, HbrDropdown] = reactWrapper(["hbr-button", "hbr-dropdown"]);

const isDarkThemeSupported = () =>
  parseVersion(window._convergence_version) >=
  VERSION_THRESHOLDS.MIN_DARK_THEME;

const UserProfile = ({ loggedInUser, waffleRef }) => {
  const { tGenerics, i18n } = useI18n("generics");
  const clUsername = getCookie("cl-user");
  const isUIconvergence = getCookie("cl-analytics");
  const isCrossLaunch = checkCookieValidity("cl-rec-apply-url");
  const isConvergenceOrCrossLaunch = isUIconvergence || isCrossLaunch;
  const [loggedUser, setLoggedUser] = React.useState(loggedInUser);
  const [clientRoles, setClientRoles] = React.useState([]);

  useEffect(() => {
    // Force light theme for convergence on older versions that don't support dark theme
    if (isUIconvergence && !isDarkThemeSupported()) {
      removeLocalStorageFlag(THEME_STORAGE_KEY);
      deleteCookie(THEME_STORAGE_KEY);
      applyTheme(ThemeModes.LIGHT);
    }
  }, [isUIconvergence]);

  // For Convergence or Cross Launch
  useEffect(() => {
    const runEffect = async () => {
      if (isUIconvergence) {
        const clientData = await apiService.getvManageClientData();

        //handle API error
        if (clientData.errorObject instanceof Object === false) {
          //only when description is present in clientData
          //replace _loggedInUser with description
          //if description is not present, _loggedInUser will remain unchanged
          setLoggedUser(
            clientData?.data?.data?.description
              ? clientData?.data?.data?.description
              : loggedUser,
          );

          const roles = clientData?.data?.data?.roles;
          if (Array.isArray(roles)) {
            setClientRoles(roles.filter(Boolean));
          }

          // Extract user locale from API response and set it for i18n
          const userLocale =
            clientData?.data?.data?.locale || clientData?.data?.data?.language;

          if (userLocale) {
            // Reinitialize locale selection using browser-first priority.
            if (window.reinitializeI18n) {
              window.reinitializeI18n(userLocale);
            } else if (i18n && i18n.changeLanguage) {
              const languageCode = userLocale.split(/[-_]/)[0].toLowerCase();
              i18n.changeLanguage(languageCode);
            }
          }
        }
      }
    };
    runEffect();
  }, []);

  // For Qualtrics
  window._vanalytics_username = clUsername
    ? clUsername
    : localStorage.getItem("user_email");
  window._current_fabric_id = isConvergenceOrCrossLaunch
    ? getCookie("cl-overlay-id")
    : localStorage.getItem("currentOverlay");
  window._current_fabric_name = isConvergenceOrCrossLaunch
    ? getCookie("cl-overlay-name")
    : localStorage.getItem("currentOverlayName");
  window._site_selected = isConvergenceOrCrossLaunch
    ? getLocalStorageFlag("currentSite")
    : window._site_selected;
  window._time_period = isConvergenceOrCrossLaunch
    ? getLocalStorageFlag("timePeriodSelected")
    : window._time_period;

  // This "Admin" user name hardcoded for cypress localhost setup
  const userName = window.location.host.includes("localhost")
    ? "Admin"
    : loggedUser.includes("@")
      ? loggedUser.split("@")[0]
      : loggedUser;
  const roleLabel =
    clientRoles.length > 0
      ? clientRoles.join(", ")
      : clUsername !== undefined
        ? clUsername.replace(/['"]/g, "")
        : userName.replace(/['"]/g, "");

  const navigateToProfile = () => {
    window.location.href = PROFILE_URL;
  };

  const removeCookies = () => {
    removeItemsLocalStorage();
    removeLocalStorageFlag("user_email");
    removeLocalStorageFlag("mfe_unified_config");
    removeLocalStorageFlag("okta-shared-transaction-storage");
    removeRumUser();
    deleteCookies();
    removeLocalStorageFlag("overlays");
    sessionStorage.removeItem("okta-transaction-storage");
  };

  const logout = async (e) => {
    if (isUIconvergence) {
      //below block is for UI convergence

      try {
        let res = await apiService.logoutVManage();

        console.log("Response for logout", res);

        window.sessionStorage.removeItem("hasShownEula");
        window.localStorage.removeItem("accessToken");
        window.localStorage.removeItem("authCode");
        window.location.href = "/login.html";

        removeCookies();
        e.preventDefault();
      } catch (err) {
        console.log("Error while calling logout", err);
      }
    } else {
      //below block is for default vA and cross launch
      try {
        const logoutCookie = getCookie("lg-vManageURI");

        let res = await apiService.logout();

        removeCookies();
        e.preventDefault();

        if (res) {
          if (logoutCookie) {
            window.location.href = logoutCookie;
          } else if (res.status === 200) {
            window.location.href = routeURLs.login;
          }
        }
      } catch (err) {
        console.log("Error while calling logout", err);
      }
    }
  };

  return (
    <div
      aria-label={tGenerics("a11yLabels.appHeaderProfileLabel")}
      data-testid="user-profile"
    >
      <HbrDropdown
        slot="user-profile"
        hoist
        menu
        sentiment="header"
        distance="12"
      >
        <HbrButton noPadding sentiment="header" slot="trigger">
          <User slot="prefix" size={22} weight="bold" />
          <CaretDown slot="suffix" weight="bold" size={16} />
          <div style={userProfileStyles.labelBold}>
            {userName.replace(/['"]/g, "")}
          </div>
        </HbrButton>
        <div className={css["profile"]} style={userProfileStyles.menu}>
          {/*Logged as*/}
          <div style={userProfileStyles.menuItem}>
            <User style={userProfileStyles.prefix} size={20} weight="bold" />
            <div style={userProfileStyles.menuBody}>
              <div style={userProfileStyles.heading}>
                {tGenerics("headerLoggedLabel")}
              </div>
              <div style={userProfileStyles.labelBold}>
                {clUsername !== undefined
                  ? clUsername.replace(/['"]/g, "")
                  : userName.replace(/['"]/g, "")}
              </div>
              {!isUIconvergence && (
                <div style={userProfileStyles.label}>
                  {loggedInUser !== undefined ? loggedInUser : ""}
                </div>
              )}
            </div>
            <HbrButton
              variant="outline"
              onClick={(e) => logout(e)}
              className={"suffix"}
              noPadding
            >
              {tGenerics("headerLogOutLabel")}
            </HbrButton>
          </div>
          {/* Show theme toggle for non-convergence OR convergence with version >= 26.2 */}
          {(!isUIconvergence || isDarkThemeSupported()) && (
            <div>
              <ThemeModeToggle />
            </div>
          )}
          {/*Role as*/}
          {isUIconvergence && (
            <div style={userProfileStyles.menuItem}>
              <Users style={userProfileStyles.prefix} size={20} weight="bold" />
              <div style={userProfileStyles.menuBody}>
                <div style={userProfileStyles.heading}>
                  {tGenerics("headerRoleAs")}
                </div>
                <div style={userProfileStyles.labelBold}>{roleLabel}</div>
              </div>
            </div>
          )}
          {/*My Profile*/}
          {isUIconvergence && (
            <div style={userProfileStyles.menuItem} onClick={navigateToProfile}>
              <UserCircle
                style={userProfileStyles.prefix}
                size={20}
                weight="bold"
              />
              <div style={userProfileStyles.menuBody}>
                <div className="hbr-type-body3 header-space">
                  {tGenerics("headerMyProfile")}
                </div>
              </div>
            </div>
          )}
        </div>
      </HbrDropdown>
      {waffleRef.current.isWaffleShown && (
        <cross-platform-navigator></cross-platform-navigator>
      )}
    </div>
  );
};

UserProfile.propTypes = {
  waffleRef: PropTypes.object,
  loggedInUser: PropTypes.string,
};

export default UserProfile;
